import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IMultiYearAvailableLanguage } from '@modeso/types__aumico-lib-annualreport-be';

/** Locale code → existing app-wide i18n key (reused from the user-edit form). */
const LOCALE_I18N_KEYS: Record<string, string> = {
    'de-CH': 'aumico_users_edit_user_general_information_languages_german_switzerland',
    'de-DE': 'aumico_users_edit_user_general_information_languages_german_germany',
    'en-US': 'aumico_users_edit_user_general_information_languages_english_us',
    'fr-CH': 'aumico_users_edit_user_general_information_languages_french_switzerland',
    'it-CH': 'aumico_users_edit_user_general_information_languages_italian_switzerland',
};

/**
 * Data passed when opening the modal — the list of locales the company has
 * eligible annual reports in, plus a count per locale. Already sorted by
 * count (highest first) by the BE.
 */
export interface MultiYearLanguageSelectionModalData {
    availableLanguages: IMultiYearAvailableLanguage[];
}

/**
 * Result returned via `MatDialogRef.close()`:
 *  - `string` (a locale): user picked → effect re-fetches with `?language=`
 *  - `null`: user dismissed (close button / backdrop / ESC) → state stays empty
 */
export type MultiYearLanguageSelectionModalResult = string | null;

/**
 * Modal that appears when the BE returns HTTP 409 `LANGUAGE_MISMATCH` for
 * the multi-year comparison endpoint — i.e. the company has annual reports
 * in 2+ different locales and the request didn't pin one via `?language=`.
 *
 * Lets the user pick which locale to scope the Mehrjahresvergleich to. The
 * choice persists in the NgRx store (per `companyId`, in-memory) so further
 * fetches reuse it; the user can also flip it later via the controls-bar
 * dropdown without re-opening this modal.
 */
@Component({
    selector: 'multi-year-language-selection-modal',
    templateUrl: './multi-year-language-selection-modal.component.html',
    styleUrls: ['./multi-year-language-selection-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiYearLanguageSelectionModalComponent {
    public selectedLanguage: string | null;

    constructor(
        @Inject(MAT_DIALOG_DATA) public readonly data: MultiYearLanguageSelectionModalData,
        private readonly dialogRef: MatDialogRef<
            MultiYearLanguageSelectionModalComponent,
            MultiYearLanguageSelectionModalResult
        >,
    ) {
        // Default-select the locale with the most reports — same order the
        // BE delivers, so just take the first entry.
        this.selectedLanguage = data.availableLanguages[0]?.lang ?? null;
    }

    /** Reuses the app-wide `aumico_users_edit_user_general_information_languages_*`
     *  family. Unknown locales return empty string → template falls back to
     *  the raw code via `defaultValue`. */
    public localeI18nKey(lang: string): string {
        return LOCALE_I18N_KEYS[lang] ?? '';
    }

    public onSelect(lang: string): void {
        this.selectedLanguage = lang;
    }

    public isSelected(lang: string): boolean {
        return this.selectedLanguage === lang;
    }

    public onConfirm(): void {
        if (this.selectedLanguage == null) return;
        this.dialogRef.close(this.selectedLanguage);
    }

    public get confirmDisabled(): boolean {
        return this.selectedLanguage == null;
    }
}
